// ==========================================================================
// Responsive Navigation
// Author: Globalia
// Licensed under the WTFPL license
// ==========================================================================

import {
    hasAttributeValue,
    toggleAttributeValue,
    addAttributeValue,
    removeAttributeValue,
    maxWidth
} from 'groots/utilities';

class GrNavigation {
    constructor(selector) {
        this.pluginName = 'grNavigation';

        // Create global references
        this.selector = typeof selector === 'string' ? document.querySelector(selector) : selector;

        // Set default parameters
        this.config = {
            trigger: '[gr-nav-trigger]',
            target: '[gr-nav-panel]',
            subtrigger: '[gr-subnav-trigger]',
            subtarget: '[gr-subnav-panel]',
        };

        // Create overlay if not already added
        if (!document.querySelector('[gr-overlay]')) {
            var overlay = document.createElement('div');
            overlay.setAttribute('gr-overlay', '');
            document.body.appendChild(overlay);
            this.overlay = overlay;
        } else {
            this.overlay = document.querySelector('[gr-overlay]');
        }

        // Init plugin
        this.init();
    }

    //  Methods
    // ----------------------------------------------------------------------

    init() {
        if (this.selector === null) {
            throw new Error(this.pluginName + ': fail - something wrong with your selector');
        } else {
            // Bind all event handlers for referencability
            var arr = ['resizeHandler', 'mouseclickHandler', 'keyupHandler'];

            for (var i = 0, len = arr.length; i < len; i++) {
                var method = arr[i];
                this[method] = this[method].bind(this);
            }

            window.addEventListener('resize', this.resizeHandler);
            document.addEventListener('click', this.mouseclickHandler);
            document.addEventListener('keyup', this.keyupHandler);

            // Flag the plugin as ready
            this.ready = true;

            toggleAttributeValue(this.selector, 'gr-nav', 'ready');

            if (window.groots.verbose) {
                console.warn(this.pluginName + ': ready');
            }
        }
    }

    resizeHandler() {
        removeAttributeValue(this.selector, 'gr-nav', 'active');
        removeAttributeValue(this.selector.querySelector(this.config.trigger), 'gr-nav-trigger', 'active');
        removeAttributeValue(this.selector.querySelector(this.config.target), 'gr-nav-panel', 'active');
        removeAttributeValue(this.selector.querySelector(this.config.subtrigger), 'gr-subnav-trigger', 'active');
        removeAttributeValue(this.selector.querySelector(this.config.subtarget), 'gr-subnav-panel', 'active');
        removeAttributeValue(this.overlay, 'gr-overlay', 'visible');
    }

    mouseclickHandler(event) {
        if (event.target.closest('[gr-nav-trigger]')) {
            event.preventDefault();

            var trigger = event.target.closest('[gr-nav-trigger]');

            toggleAttributeValue(this.selector, 'gr-nav', 'active');
            toggleAttributeValue(trigger, 'gr-nav-trigger', 'active');
            toggleAttributeValue(this.selector.querySelector('[gr-nav-panel]'), 'gr-nav-panel', 'active');

            // Remove subnav triggers and panels active states
            var subNavTriggers = this.selector.querySelectorAll('[gr-subnav-trigger]');
            var subNavPanels = this.selector.querySelectorAll('[gr-subnav-panel]');

            for (var i = subNavTriggers.length - 1; i >= 0; i--) {
                removeAttributeValue(subNavTriggers[i], 'gr-subnav-trigger', 'active', true);
            }

            for (var i = subNavPanels.length - 1; i >= 0; i--) {
                removeAttributeValue(subNavPanels[i], 'gr-subnav-panel', 'active', true);
            }

            toggleAttributeValue(this.overlay, 'gr-overlay', 'visible');
        } else if (event.target.closest('[gr-subnav-trigger]')) {
            event.preventDefault();

            var trigger = event.target.closest('[gr-subnav-trigger]');
            var canTrigger = true;
            var isActive = false;
            var values = trigger.getAttribute('gr-subnav-trigger').split(' ');
            var id = '';
            var breakpoint = '';

            // Get id and breakpoint attributes values
            if (values[0] !== '') {
                for (var i = 0, len = values.length; i < len; i++) {
                    var attr = values[i];

                    if (Object.keys(window.groots.breakpoints).includes(attr)) {
                        breakpoint = attr;
                    } else if (attr === 'active') {
                        isActive = true;
                    } else {
                        id = attr;
                    }
                }
            }

            // If breakpoint is set, check if subnav can be triggered on click
            if (breakpoint !== '') {
                canTrigger = maxWidth(breakpoint);
            }

            if (canTrigger) {
                if (isActive) {
                    if (id !== '') {
                        // Toggle all triggers that have this id
                        var subNavTriggerswithID = this.selector.querySelectorAll('[gr-subnav-trigger~="' + id + '"]');
                        for (var i = subNavTriggerswithID.length - 1; i >= 0; i--) {
                            removeAttributeValue(subNavTriggerswithID[i], 'gr-subnav-trigger', 'active', true);
                        }

                        // Match the panel id
                        removeAttributeValue(this.selector.querySelector('[gr-subnav-panel~="' + id + '"]'), 'gr-subnav-panel', 'active', true);
                    } else {
                        // Toggle the trigger
                        removeAttributeValue(trigger, 'gr-subnav-trigger', 'active', true);
                        // Toggle the next sibling which is the panel
                        removeAttributeValue(trigger.nextElementSibling, 'gr-subnav-panel', 'active', true);
                    }

                    // Remove overlay if subnav is not open in desktop menu
                    if (!hasAttributeValue(this.selector.querySelector(this.config.target), 'gr-nav-panel', 'active')) {
                        removeAttributeValue(this.overlay, 'gr-overlay', 'visible');
                    }
                } else {
                    // Remove subnav triggers and panels active states
                    var subNavTriggers = this.selector.querySelectorAll('[gr-subnav-trigger]');
                    var subNavPanels = this.selector.querySelectorAll('[gr-subnav-panel]');

                    for (var i = subNavTriggers.length - 1; i >= 0; i--) {
                        removeAttributeValue(subNavTriggers[i], 'gr-subnav-trigger', 'active', true);
                    }

                    for (var i = subNavPanels.length - 1; i >= 0; i--) {
                        removeAttributeValue(subNavPanels[i], 'gr-subnav-panel', 'active', true);
                    }

                    if (id !== '') {
                        // Toggle all triggers that have this id
                        var subNavTriggerswithID = this.selector.querySelectorAll('[gr-subnav-trigger~="' + id + '"]');
                        for (var i = subNavTriggerswithID.length - 1; i >= 0; i--) {
                            addAttributeValue(subNavTriggerswithID[i], 'gr-subnav-trigger', 'active', true);
                        }

                        // Match the panel id
                        addAttributeValue(this.selector.querySelector('[gr-subnav-panel~="' + id + '"]'), 'gr-subnav-panel', 'active', true);
                    } else {
                        // Toggle the trigger
                        addAttributeValue(trigger, 'gr-subnav-trigger', 'active', true);
                        // Toggle the next sibling which is the panel
                        addAttributeValue(trigger.nextElementSibling, 'gr-subnav-panel', 'active', true);
                    }

                    // Add overlay if subnav is not open in desktop menu
                    if (!hasAttributeValue(this.selector.querySelector(this.config.target), 'gr-nav-panel', 'active')) {
                        addAttributeValue(this.overlay, 'gr-overlay', 'visible');
                    }
                }
            }
        } else if (event.target === this.overlay) {
            removeAttributeValue(this.selector, 'gr-nav', 'active');
            removeAttributeValue(this.selector.querySelector(this.config.trigger), 'gr-nav-trigger', 'active');
            removeAttributeValue(this.selector.querySelector(this.config.target), 'gr-nav-panel', 'active');

            // Remove subnav triggers and panels active states
            var subNavTriggers = this.selector.querySelectorAll('[gr-subnav-trigger]');
            var subNavPanels = this.selector.querySelectorAll('[gr-subnav-panel]');

            for (var i = subNavTriggers.length - 1; i >= 0; i--) {
                removeAttributeValue(subNavTriggers[i], 'gr-subnav-trigger', 'active', true);
            }

            for (var i = subNavPanels.length - 1; i >= 0; i--) {
                removeAttributeValue(subNavPanels[i], 'gr-subnav-panel', 'active', true);
            }

            removeAttributeValue(this.overlay, 'gr-overlay', 'visible');
        }
    }

    keyupHandler(event) {
        event.preventDefault();
        if (event.keyCode === 27) {
            removeAttributeValue(this.selector, 'gr-nav', 'active');
            removeAttributeValue(this.selector.querySelector(this.config.trigger), 'gr-nav-trigger', 'active');
            removeAttributeValue(this.selector.querySelector(this.config.target), 'gr-nav-panel', 'active');
            removeAttributeValue(this.selector.querySelector(this.config.subtrigger), 'gr-subnav-trigger', 'active');
            removeAttributeValue(this.selector.querySelector(this.config.subtarget), 'gr-subnav-panel', 'active');
            removeAttributeValue(this.overlay, 'gr-overlay', 'visible');
        }
    }

    // Destroy - remove listeners to prevent from memory leak
    destroy() {
        window.removeEventListener('resize', this.resizeHandler);
        document.removeEventListener('click', this.mouseclickHandler);
        document.removeEventListener('keyup', this.keyupHandler);
        removeAttributeValue(this.selector, 'gr-nav', 'ready active');
        removeAttributeValue(this.selector.querySelector(this.config.trigger), 'gr-nav-trigger', 'active');
        removeAttributeValue(this.selector.querySelector(this.config.target), 'gr-nav-panel', 'active');
        removeAttributeValue(this.selector.querySelector(this.config.subtrigger), 'gr-subnav-trigger', 'active');
        removeAttributeValue(this.selector.querySelector(this.config.subtarget), 'gr-subnav-panel', 'active');
        removeAttributeValue(this.overlay, 'gr-overlay', 'visible');

        if (window.groots.verbose) {
            console.warn(this.pluginName + ': destroyed');
        }
    }
}

export default GrNavigation;

//  Auto-Instantiation
// ----------------------------------------------------------------------

(function() {
    const all = document.querySelectorAll('[gr-nav]');
    window.groots.plugins.grNavigation = {};

    if (all.length) {
        window.groots.plugins.grNavigation.instances = {};
        let index = -1;

        while (all[++index]) {
            window.groots.plugins.grNavigation.instances[index] = new GrNavigation(all[index]);
        }
    }
}());
